/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import InlineMessage from '@atlaskit/inline-message';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { type StoreError } from '@atlassian/conversation-assistant-store/controllers/chat-signals';

import messages from './messages';

const styles = cssMap({
	content: {
		paddingInline: token('space.100'),
		paddingTop: token('space.100'),
	},
});

type StoreRateLimitWarning = {
	error?: StoreError;
	removeRateLimiting: () => void;
};

// Default to 30s if nothing in the payload
const DISPLAY_DURATION_IN_SECONDS = 30;

export const StoreRateLimitWarning = ({
	removeRateLimiting,
}: StoreRateLimitWarning): JSX.Element => {
	const [secondsRemaining, setSecondsRemaining] = useState(DISPLAY_DURATION_IN_SECONDS);
	const { formatMessage } = useIntl();
	const intervalRef = useRef<ReturnType<typeof setInterval>>();
	const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

	useEffect(() => {
		timeoutRef.current = setTimeout(() => {
			removeRateLimiting();
		}, DISPLAY_DURATION_IN_SECONDS * 1000);
		intervalRef.current = setInterval(() => {
			setSecondsRemaining((state) => state - 1);
		}, 1000);

		return () => {
			clearTimeout(timeoutRef.current);
			clearInterval(intervalRef.current);
		};
	}, [removeRateLimiting]);

	return (
		<Box xcss={styles.content} testId="conversation-assistant.store-rate-limit-warning">
			<InlineMessage
				appearance="warning"
				secondaryText={formatMessage(messages.rateLimitWarning, {
					secondsRemaining,
				})}
			>
				{formatMessage(messages.rateLimitWarningDescription)}
			</InlineMessage>
		</Box>
	);
};
